import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { ROW_BORDER_BOTTOM_PX, UserTimelines } from '@wilson/interfaces';
import {
  DatesDemarcatorLinesComponent,
  RenderData,
  WeekendHolidayHighlighterComponent,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DragAndDropModeService,
  TimelineInteractionService,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  AssignedRegionVisibilityStateService,
} from '@wilson/shift-timeline/state';
import {
  Observable,
  BehaviorSubject,
  map,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { MoveAbsenceDropZoneCellViewComponent } from '../../components/move-absence-drop-zone-cell-view/move-absence-drop-zone-cell-view.component';
import { UserTimelinesInfoCardComponent } from '../../components/user-timelines-info-card/user-timelines-info-card.component';
import { ToUserAssignmentStacksPipe } from '../../pipes/to-user-assignment-stacks.pipe';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../services/timeline-shift-drag-and-drop-directive-helper.service';
import { MoveShiftDropZoneCellViewComponent } from '../move-shift-drop-zone-cell-view/move-shift-drop-zone-cell-view.component';
import { TimelineCellViewUserRowAssignmentsComponent } from '../timeline-cell-view-user-row-assignments/timeline-cell-view-user-row-assignments.component';
import { ListRange } from '@angular/cdk/collections';

@Component({
  selector: 'wilson-timeline-cell-view-user-row',
  templateUrl: './timeline-cell-view-user-row.component.html',
  styleUrls: ['./timeline-cell-view-user-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TimelineInteractionService],
  imports: [
    CommonModule,
    ScrollingModule,
    ToUserAssignmentStacksPipe,
    MoveShiftDropZoneCellViewComponent,
    UserTimelinesInfoCardComponent,
    TimelineCellViewUserRowAssignmentsComponent,
    DatesDemarcatorLinesComponent,
    WeekendHolidayHighlighterComponent,
    MoveAbsenceDropZoneCellViewComponent,
  ],
})
export class TimelineCellViewUserRowComponent implements OnInit {
  timelineInteractionService = inject(TimelineInteractionService);
  private readonly store = inject(Store);
  protected assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );
  protected readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  protected readonly dragAndDropModeService = inject(DragAndDropModeService);

  public resourcesColumnWidthPx$!: Observable<number>;
  public frameLength$!: Observable<number>;
  public ROW_BORDER_BOTTOM_PX = ROW_BORDER_BOTTOM_PX;
  public renderRadioInput = false;

  private listRangeSubject = new BehaviorSubject<ListRange>({
    start: 0,
    end: 25,
  });
  private originalUsersSubject = new BehaviorSubject<UserTimelines[]>([]);
  protected trimmedUserList$ = combineLatest([
    this.listRangeSubject,
    this.originalUsersSubject,
  ]).pipe(
    debounceTime(10),
    map(([listRange, users]) => users.slice(0, listRange.end)), // load all users starting from 0 to provide smooth scrolling experience
    distinctUntilChanged(),
  );

  @Input()
  rowHeightWithBorder!: number;

  @Input()
  set theoreticalListRange(value: ListRange) {
    if (value) {
      this.listRangeSubject.next(value);
    }
  }

  @Input()
  stickyHasShadow?: boolean;
  fullUserLength = 0;

  @Input() set users(value: UserTimelines[] | null) {
    if (value) {
      this.originalUsersSubject.next(value);
      this.fullUserLength = value.length;
    } else {
      this.originalUsersSubject.next([]);
    }
  }

  @Input()
  viewport!: HTMLElement;

  @Input()
  weekendHolidayRenderData!: RenderData[];

  defaultRowHeight$!: Observable<number>;

  publicHolidaysRegion$ = this.store.select(AuthState.publicHolidaysRegion);
  visibleDaysRange$ = this.store.select(
    ShiftTimelineDataState.visibleDateRange,
  );
  timeframe$ = this.store.select(ShiftTimelineDataState.timeframe);

  ngOnInit(): void {
    this.resourcesColumnWidthPx$ = this.store.select(
      ShiftTimelineSettingsState.resourcesColumnWidthPx,
    );

    this.defaultRowHeight$ = this.store.select(
      ShiftTimelineSettingsState.defaultRowHeight,
    );

    this.frameLength$ = this.store.select(ShiftTimelineState.frameLength);
  }

  trackById(_: number, user: UserTimelines): string | number | undefined {
    return user.id;
  }
}
